import React, { useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";

// Only import CSS in browser environment
if (typeof window !== "undefined") {
  require("../../css/auth.css");
}

const AUTH_ENABLED = process.env.GATSBY_AUTH_ENABLED === "true";
const AUTH_COOKIE_NAME = "puppy_growth_auth";
const AUTH_COOKIE_EXPIRY_HOURS = 4;

/**
 * Set a cookie with a given name, value, and expiry in hours.
 */
const setCookie = (name, value, hours) => {
  const expires = new Date(
    Date.now() + hours * 60 * 60 * 1000
  ).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Strict`;
};

/**
 * Read a cookie by name. Returns the decoded value or null.
 */
const getCookie = (name) => {
  const match = document.cookie.match(
    new RegExp("(^|;\\s*)" + name + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[2]) : null;
};

/**
 * Check whether a valid auth cookie exists.
 */
const hasValidAuthCookie = () => {
  if (typeof document === "undefined") return false;
  try {
    const raw = getCookie(AUTH_COOKIE_NAME);
    if (!raw) return false;
    const data = JSON.parse(raw);
    return data && data.success === true;
  } catch {
    return false;
  }
};

const AuthGate = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // On first render, check for an existing valid cookie
    return hasValidAuthCookie();
  });
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // If auth is not enabled, render children directly
  if (!AUTH_ENABLED) {
    return <>{children}</>;
  }

  // If already authenticated, render children
  if (isAuthenticated) {
    return <>{children}</>;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!username.trim() || !password.trim()) {
      setError("Please enter both username and password.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.GATSBY_BASE_URL}/api/v1/authenticate`,
        { username, password }
      );

      if (response.data?.success) {
        // Store the API response in a cookie that expires in a few hours
        setCookie(
          AUTH_COOKIE_NAME,
          JSON.stringify(response.data),
          AUTH_COOKIE_EXPIRY_HOURS
        );
        setIsAuthenticated(true);
      } else {
        setError(
          response.data?.message || "Authentication failed. Please try again."
        );
      }
    } catch (err) {
      if (err.response) {
        setError(
          err.response.data?.message ||
            "Invalid credentials. Please try again."
        );
      } else if (err.request) {
        setError("Unable to reach the server. Please try again later.");
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="auth-gate-overlay">
      <div className="auth-gate-container">
        <div className="auth-gate-card">
          <h2 className="auth-gate-title">Login</h2>
          <p className="auth-gate-subtitle">
            Please enter your credentials to continue
          </p>

          <form onSubmit={handleSubmit} className="auth-gate-form">
            <div className="auth-gate-field">
              <label htmlFor="auth-username" className="auth-gate-label">
                Username
              </label>
              <input
                id="auth-username"
                type="text"
                className="auth-gate-input"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                disabled={isLoading}
                autoComplete="username"
                autoFocus
              />
            </div>

            <div className="auth-gate-field">
              <label htmlFor="auth-password" className="auth-gate-label">
                Password
              </label>
              <input
                id="auth-password"
                type="password"
                className="auth-gate-input"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                autoComplete="current-password"
              />
            </div>

            {error && <div className="auth-gate-error">{error}</div>}

            <button
              type="submit"
              className="auth-gate-button"
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="auth-gate-spinner"></span>
              ) : (
                "Submit"
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

AuthGate.propTypes = {
  children: PropTypes.node.isRequired,
};

export default AuthGate;
